import BoltIcon from '@mui/icons-material/Bolt';
import { Tooltip } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectHasExpressModeMealPeriods,
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../selectors/menu';
import { VisualIndicator } from '../VisualIndicator';

const TOOLTIP_ON =
  'Express mode is ON — upsells are suppressed (active express-mode meal period or the order timer has elapsed).';
const TOOLTIP_OFF = 'Express mode is OFF — upsells follow normal behavior.';

export const ExpressUpsellIndicator: FC = () => {
  const hasExpressModeMealPeriods = useAppSelector(
    selectHasExpressModeMealPeriods
  );
  // Express mode has two independent triggers that both suppress upsells: an
  // active express-mode meal period (wall-clock) and the express order timer
  // elapsing (HITL-1359). The bolt reflects either.
  const isExpressUpsellMode = useAppSelector(selectIsExpressUpsellMode);
  const isExpressTimerActive = useAppSelector(selectIsExpressTimerActive);
  const expressActive = isExpressUpsellMode || isExpressTimerActive;

  // Force a re-evaluation every 60s so the indicator flips when the wall
  // clock crosses an express-mode meal-period boundary even if no other
  // state changes.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasExpressModeMealPeriods) return;
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [hasExpressModeMealPeriods]);

  // Render when either trigger can apply: the restaurant has express meal
  // periods, OR the express timer has elapsed (which suppresses all upsells
  // regardless of meal periods).
  if (!hasExpressModeMealPeriods && !isExpressTimerActive) return null;

  return (
    <Tooltip title={expressActive ? TOOLTIP_ON : TOOLTIP_OFF} arrow>
      {/* span wrapper so MUI Tooltip works even when the child is disabled */}
      <span>
        <VisualIndicator
          IconComponent={BoltIcon}
          isActive={expressActive}
          data-testid="express-upsell-mode-indicator"
        />
      </span>
    </Tooltip>
  );
};
